import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Users,
  UserCog,
  Wallet,
  LayoutDashboard,
  KeyRound,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const ADMIN_NAV = [
  { to: "/ipo", label: "Data IPO", icon: TrendingUp, id: "ipo" },
  { to: "/pengguna", label: "Manajemen Pengguna", icon: UserCog, id: "pengguna" },
  { to: "/anggota", label: "Data Anggota", icon: Users, id: "anggota" },
  { to: "/saldo", label: "Saldo Anggota", icon: Wallet, id: "saldo" },
  { to: "/dashboard", label: "Dashboard Anggota", icon: LayoutDashboard, id: "dashboard" },
  { to: "/direktori", label: "Direktori Akun", icon: KeyRound, id: "direktori" },
];

const MEMBER_NAV = [
  { to: "/ipo", label: "Data IPO", icon: TrendingUp, id: "ipo" },
  { to: "/dashboard", label: "Dashboard Saya", icon: LayoutDashboard, id: "dashboard" },
  { to: "/direktori", label: "Direktori Akun", icon: KeyRound, id: "direktori" },
];

export default function Shell({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const nav = isAdmin ? ADMIN_NAV : MEMBER_NAV;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavItems = () => (
    <nav className="flex flex-col gap-1">
      {nav.map((item) => (
        <NavLink
          key={item.id}
          to={item.to}
          data-testid={`sidebar-nav-${item.id}`}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
              isActive
                ? "bg-[#00d09c]/10 text-[#00d09c]"
                : "text-[#8f9bb3] hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <item.icon size={17} strokeWidth={1.75} />
          <span className="truncate">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="relative z-10 flex min-h-screen">
      <aside className="hidden w-[264px] shrink-0 flex-col border-r border-[#1e2536] bg-[#0b0f16] px-5 py-7 lg:flex">
        <Brand />
        <div className="mt-9 flex-1">
          <p className="mb-3 px-3 text-xs uppercase tracking-[0.2em] text-[#4d586f]">Menu</p>
          <NavItems />
        </div>
        <UserBox user={user} onLogout={handleLogout} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="glass sticky top-0 z-30 flex items-center justify-between border-b px-5 py-4 lg:px-8">
          <button
            data-testid="mobile-menu-btn"
            className="text-[#8f9bb3] lg:hidden"
            onClick={() => setOpen(true)}
          >
            <Menu size={20} />
          </button>
          <div className="hidden lg:block">
            <p className="text-xs uppercase tracking-[0.2em] text-[#4d586f]">Bursa Efek Indonesia</p>
            <h4 className="text-lg text-white">Terminal Investasi</h4>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p data-testid="header-username" className="text-sm text-white">{user?.username}</p>
              <p className="text-xs uppercase tracking-widest text-[#00d09c]">{user?.role}</p>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[#00d09c]/40 bg-[#00d09c]/10 font-mono-num text-sm text-[#00d09c]">
              {(user?.username || "?").slice(0, 2).toUpperCase()}
            </div>
          </div>
        </header>
        <motion.main
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="min-w-0 flex-1 p-5 lg:p-8"
        >
          {children}
        </motion.main>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[270px] border-r border-[#1e2536] bg-[#0b0f16] px-5 py-7">
            <div className="flex items-center justify-between">
              <Brand />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-[#8f9bb3]">
                <X size={19} />
              </button>
            </div>
            <div className="mt-8">
              <NavItems />
            </div>
            <div className="mt-8">
              <UserBox user={user} onLogout={handleLogout} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Brand = () => (
  <div className="flex items-center gap-2.5">
    <img
      src="https://customer-assets-jai6qajn.emergentagent.net/job_stock-invest-hub-3/artifacts/mw294pcw_harimau-removebg-preview.png"
      alt="Cuan IPO"
      className="h-10 w-10 shrink-0 object-contain"
    />
    <div>
      <p className="text-base font-bold leading-none tracking-tight text-white">Cuan IPO</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[#4d586f]">Investment Desk</p>
    </div>
  </div>
);

const UserBox = ({ user, onLogout }) => (
  <div className="rounded-lg border border-[#1e2536] bg-[#10141d] p-4">
    <p className="truncate text-sm text-white">{user?.username}</p>
    <p className="mt-0.5 text-xs text-[#8f9bb3]">{user?.nama_panggilan || "Akun Pengelola"}</p>
    <Button
      data-testid="logout-btn"
      onClick={onLogout}
      variant="outline"
      className="mt-3 w-full border-[#1e2536] bg-transparent text-[#8f9bb3] transition-colors duration-200 hover:bg-[#ff3b30]/10 hover:text-[#ff3b30]"
    >
      <LogOut size={15} className="mr-2" /> Keluar
    </Button>
  </div>
);
